import type { TFunction } from 'i18next';

/**
 * Etykiety płatności zamówienia — wspólne dla tablicy, szczegółów i dashboardu.
 *
 * Trzy ekrany pokazują tę samą informację i rozjeżdżały się już raz (kanban spłaszczał
 * `online` do „karta"). Jedno źródło prawdy zamiast trzech kopii warunków.
 */

export interface OrderPaymentLike {
  paymentMethod?: string;
  /** Ustawiany wyłącznie przez webhook Stripe'a. Brak przy `online` = nieopłacone. */
  paymentStatus?: string;
  /**
   * Czym faktycznie zapłacono: `card`, `blik`, `p24`, `klarna`, `link`…
   * `null` przechodzi tak samo jak brak pola: rejestr płatności (`stripeFees`) zwraca
   * puste szczegóły jako `null`, a zamówienia po prostu ich nie mają.
   */
  paymentDetailType?: string | null;
  paymentCardBrand?: string | null;
  paymentCardLast4?: string | null;
  /** `apple_pay` / `google_pay`, gdy karta poszła przez portfel. */
  paymentWallet?: string | null;
}

export type PaymentTone = 'ok' | 'warn' | 'bad';

/**
 * Płatność trwa po stronie banku: BLIK czeka na potwierdzenie w aplikacji, Przelewy24 na
 * przelew, karta na 3D Secure (#195). To nie jest ani wpłata, ani błąd: zamówienie czeka,
 * a hotel nie powinien go realizować ani liczyć do przychodu.
 * LUSTRO: `PAYMENT_IN_PROGRESS_STATUSES` z functions-payments / functions-outlets.
 */
export const PAYMENT_IN_PROGRESS_STATUSES: readonly string[] = ['processing', 'requires_action'];

/**
 * Tyle backend trzyma zasób (termin, sprzęt, sztuki) ponad okno płatności, gdy płatność
 * jest w toku. LUSTRO: `PAYMENT_IN_PROGRESS_GRACE_MS` (#195).
 */
export const PAYMENT_IN_PROGRESS_GRACE_MS = 30 * 60 * 1000;

export const isPaymentInProgress = (paymentStatus: unknown): boolean => (
  typeof paymentStatus === 'string' && PAYMENT_IN_PROGRESS_STATUSES.includes(paymentStatus)
);

/** Koniec okna płatności z łaską dla płatności w toku (lustro `holdEndMs` z backendu). */
export const paymentDeadlineWithGrace = (deadlineMs: number | null, paymentStatus: unknown): number | null => (
  deadlineMs === null ? null : deadlineMs + (isPaymentInProgress(paymentStatus) ? PAYMENT_IN_PROGRESS_GRACE_MS : 0)
);

/**
 * Wpłata online dotarła, a zamówienie jest w obiegu: także po częściowym zwrocie i w trakcie
 * sporu. Oba stany mogą się pojawić przy zamówieniu jeszcze `new` (#195), więc warunek
 * „tylko `paid`" chował takie zamówienie z tablicy i z liczników nowych.
 */
export const CAPTURED_PAYMENT_STATUSES: readonly string[] = ['paid', 'partiallyRefunded', 'disputed'];

export const isPaymentCaptured = (paymentStatus: unknown): boolean => (
  typeof paymentStatus === 'string' && CAPTURED_PAYMENT_STATUSES.includes(paymentStatus)
);

/** Spór zamknięty na korzyść hotelu: pieniądze zostały, `paymentStatus` wrócił do `paid`. */
const DISPUTE_WON_STATUSES = new Set(['won', 'warning_closed']);

export const isDisputeWon = (disputeStatus: unknown): boolean => DISPUTE_WON_STATUSES.has(String(disputeStatus ?? ''));

/** Podpowiedź do „Opłacone" po wygranym sporze: jedno zdanie dla wszystkich widoków. */
export const disputeWonHint = (t: TFunction): string => String(t(
  'orders.paymentDisputeWonHint',
  'Gość zgłosił sporną płatność, ale spór zakończył się na korzyść hotelu. Pieniądze zostają na koncie.',
));

/** Pola zamówienia, które zmieniają znaczenie stanu płatności (anulowanie, spór). */
export interface PaymentContextLike {
  /** Status zamówienia (`cancelled`, `new`…). */
  status?: string | null;
  /** Zamówienie wygaszone po oknie płatności (cron albo webhook spóźnionej wpłaty). */
  paymentExpired?: boolean | null;
  /** Stan ostatniego sporu ze Stripe'a (`won`, `warning_closed`, `lost`…). */
  disputeStatus?: string | null;
}

const WALLET_LABELS: Record<string, string> = {
  apple_pay: 'Apple Pay',
  google_pay: 'Google Pay',
  link: 'Link',
};

const INSTRUMENT_LABELS: Record<string, string> = {
  blik: 'BLIK',
  p24: 'Przelewy24',
  klarna: 'Klarna',
  link: 'Link',
  bancontact: 'Bancontact',
  eps: 'EPS',
};

/**
 * „BLIK" albo „Visa ···· 4242" — z samych szczegółów płatności, bez pytania o metodę.
 * Rejestr `stripeFees` (widok płatności) nie niesie `paymentMethod`: każdy jego wpis
 * jest z definicji online, więc warunek z zamówienia nie miałby na czym stanąć.
 */
export const paymentDetailsLabel = (details: Omit<OrderPaymentLike, 'paymentMethod'>): string | null => {
  // Portfel ma pierwszeństwo: dla gościa „Apple Pay" znaczy więcej niż „Visa".
  if (details.paymentWallet) return WALLET_LABELS[details.paymentWallet] ?? details.paymentWallet;

  const type = details.paymentDetailType;
  if (!type) return null;
  if (type === 'card') {
    const brand = details.paymentCardBrand
      ? details.paymentCardBrand.charAt(0).toUpperCase() + details.paymentCardBrand.slice(1)
      : 'Karta';
    return details.paymentCardLast4 ? `${brand} ···· ${details.paymentCardLast4}` : brand;
  }
  return INSTRUMENT_LABELS[type] ?? type;
};

/** To samo, ale dla zamówienia — przy gotówce i karcie przy odbiorze instrumentu nie ma. */
export const paymentInstrumentLabel = (order: OrderPaymentLike): string | null => (
  order.paymentMethod === 'online' ? paymentDetailsLabel(order) : null
);

/** Metoda płatności, z dopiskiem o instrumencie przy płatności online. */
export const paymentMethodLabel = (order: OrderPaymentLike, t: TFunction): string => {
  if (order.paymentMethod === 'cash') return String(t('orders.paymentCash', 'Gotówka'));
  if (order.paymentMethod === 'card') return String(t('orders.paymentCard', 'Karta przy odbiorze'));
  if (order.paymentMethod !== 'online') return '—';

  const online = String(t('orders.paymentOnline', 'Płatność online'));
  const instrument = paymentInstrumentLabel(order);
  return instrument ? `${online} · ${instrument}` : online;
};

/**
 * Stan płatności online z tonem koloru. `null` przy gotówce i karcie przy odbiorze —
 * te rozliczają się poza systemem, więc nie mamy o nich wiedzy, a pokazanie statusu
 * byłoby wprowadzaniem w błąd.
 */
export const paymentStateBadge = (
  order: OrderPaymentLike & PaymentContextLike,
  t: TFunction,
): PaymentStateLabel | null => {
  if (order.paymentMethod !== 'online') return null;
  const paymentStatus = order.paymentStatus ?? '';

  if (isPaymentInProgress(paymentStatus)) {
    return {
      label: String(t('orders.paymentInProgress', 'Płatność w toku')),
      tone: 'warn',
      hint: String(t(
        'orders.paymentInProgressHint',
        'Bank jeszcze potwierdza płatność (BLIK, Przelewy24 lub 3D Secure). Zamówienie wejdzie do realizacji, gdy wpłata zostanie zaksięgowana.',
      )),
    };
  }

  /* Zwrot albo przegrany spór, zanim zamówienie weszło do obiegu (#195): wygaszone
     zamówienie zostaje `cancelled`, a `paymentStatus` zachowuje stan zwrotu. Samo
     „Zwrócone" na czerwono sugerowało hotelowi, że coś poszło nie tak. */
  if (order.status === 'cancelled' && order.paymentExpired === true
    && (paymentStatus === 'refunded' || paymentStatus === 'disputeLost')) {
    return {
      label: String(t('orders.paymentCancelledRefunded', 'Anulowane, zwrócono')),
      tone: paymentStatus === 'refunded' ? 'ok' : 'bad',
      hint: paymentStatus === 'refunded'
        ? String(t(
          'orders.paymentCancelledRefundedHint',
          'Zamówienie wygasło przed potwierdzeniem płatności, a pieniądze wróciły do gościa. Nie trzeba nic robić.',
        ))
        : String(t(
          'orders.paymentCancelledDisputeLostHint',
          'Zamówienie wygasło przed potwierdzeniem płatności, a gość odzyskał pieniądze przez bank (spór przegrany).',
        )),
    };
  }

  if (paymentStatus === 'paid' && isDisputeWon(order.disputeStatus)) {
    return { label: String(t('orders.paymentPaid', 'Opłacone')), tone: 'ok', hint: disputeWonHint(t) };
  }

  const map: Record<string, { label: string; tone: PaymentTone }> = {
    paid: { label: String(t('orders.paymentPaid', 'Opłacone')), tone: 'ok' },
    failed: { label: String(t('orders.paymentFailed', 'Nieudana')), tone: 'bad' },
    refunded: { label: String(t('orders.paymentRefunded', 'Zwrócone')), tone: 'bad' },
    partiallyRefunded: { label: String(t('orders.paymentPartiallyRefunded', 'Częściowo zwrócone')), tone: 'warn' },
    disputed: { label: String(t('orders.paymentDisputed', 'Spór')), tone: 'bad' },
    disputeLost: { label: String(t('orders.paymentDisputeLost', 'Spór przegrany')), tone: 'bad' },
    expired: { label: String(t('orders.paymentExpired', 'Czas na płatność minął')), tone: 'bad' },
  };

  return map[paymentStatus] ?? {
    label: String(t('orders.paymentPending', 'Oczekuje na płatność')),
    tone: 'warn',
  };
};

/** Znacznik czasu z Firestore (`Timestamp`), po serializacji albo jako `Date`. */
export type TimestampLike = { seconds?: number; toMillis?: () => number } | Date | null | undefined;

/**
 * Wpłata, która dotarła PO terminie płatności albo po zamknięciu zamówienia (backend #196).
 *
 * Webhook nie wpuszcza jej do obiegu: zamówienie zostaje anulowane, płatność dostaje
 * `paid` + `paidAfterExpiry`, a pieniądze wracają do gościa automatycznie. Bez osobnej
 * etykiety recepcja widziała „anulowane + opłacone" i nie wiedziała, czy ma coś zrobić.
 */
export interface LatePaymentLike {
  paidAfterExpiry?: boolean | null;
  /** `orderClosed` (zamówienie już zamknięte) albo `deadlinePassed` (minęło okno płatności). */
  paidAfterExpiryReason?: string | null;
  paymentStatus?: string | null;
  /** Stan ostatniego zwrotu: webhook `charge.refunded`, a `failed` także nieudane zlecenie (#242). */
  refundStatus?: string | null;
  /** Kiedy webhook zapisał wpłatę; od tej chwili liczy się czas na zwrot automatyczny. */
  paidAt?: TimestampLike;
  paymentUpdatedAt?: TimestampLike;
  updatedAt?: TimestampLike;
}

export type LatePaymentState =
  /** Pieniądze już wróciły do gościa. */
  | 'refunded'
  /** Zwrot automatyczny zlecony albo w drodze: hotel nic nie robi. */
  | 'refunding'
  /** Automatyczny zwrot się nie udał: zostaje ręczny zwrot z panelu. */
  | 'refundFailed'
  /** Zwrot nie dał znaku życia w rozsądnym czasie: trzeba sprawdzić w Stripe. */
  | 'refundDelayed';

/**
 * Po tylu minutach bez żadnego stanu zwrotu uznajemy, że zwrot automatyczny utknął.
 * Zwrot zlecany jest w tym samym webhooku, a `charge.refunded` przychodzi zwykle po sekundach,
 * więc pół godziny to bezpieczny zapas. Siatka bezpieczeństwa na wypadek, gdy backend
 * nie zdążył zapisać `refundStatus: "failed"`.
 */
export const LATE_REFUND_GRACE_MS = 30 * 60 * 1000;

const millisOf = (value: TimestampLike): number | null => {
  if (!value) return null;
  if (value instanceof Date) return value.getTime();
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  return null;
};

/**
 * Stan spóźnionej wpłaty albo `null`, gdy wpłata nie była spóźniona LUB jej los
 * opisuje zwykła etykieta płatności. Spór (`disputed`/`disputeLost`) i zwrot częściowy
 * to sprawy, w których coś zadziało się poza zwrotem automatycznym, więc dostają
 * standardowe „Spór", „Spór przegrany", „Częściowo zwrócone".
 */
export const latePaymentState = (
  order: LatePaymentLike | null | undefined,
  nowMs: number = Date.now(),
): LatePaymentState | null => {
  if (order?.paidAfterExpiry !== true) return null;
  const paymentStatus = String(order.paymentStatus ?? '');
  if (paymentStatus === 'refunded') return 'refunded';
  if (paymentStatus !== 'paid') return null;
  if (order.refundStatus === 'failed') return 'refundFailed';
  if (order.refundStatus) return 'refunding';

  const since = millisOf(order.paidAt) ?? millisOf(order.paymentUpdatedAt) ?? millisOf(order.updatedAt);
  if (since !== null && nowMs - since > LATE_REFUND_GRACE_MS) return 'refundDelayed';
  return 'refunding';
};

/**
 * Czy zwrot tej wpłaty załatwia system. `true` = nie pokazujemy zwrotu jako zadania
 * dla hotelu (licznik „Do zwrotu", przycisk „Zwrot środków"). Nieudany albo opóźniony
 * zwrot automatyczny wraca do hotelu jak każdy inny.
 */
export const isAutoRefundHandled = (order: LatePaymentLike | null | undefined, nowMs?: number): boolean => {
  const state = latePaymentState(order, nowMs);
  return state === 'refunded' || state === 'refunding';
};

/**
 * Czy stan tej wpłaty może się zmienić sam, tylko z upływem czasu („w toku” → „opóźnia się”).
 * Tylko wtedy ekran potrzebuje zegara: inne stany zmienia wyłącznie zapis z webhooka.
 */
export const needsLateRefundClock = (order: LatePaymentLike | null | undefined, nowMs?: number): boolean => (
  !order?.refundStatus && latePaymentState(order, nowMs) === 'refunding'
);

export interface PaymentStateLabel {
  label: string;
  tone: PaymentTone;
  /** Wyjaśnienie do podpowiedzi (tooltip) albo do opisu w szczegółach. */
  hint?: string;
}

/** Etykieta i wyjaśnienie spóźnionej wpłaty; `null`, gdy wpłata nie była spóźniona. */
export const latePaymentLabel = (
  order: LatePaymentLike | null | undefined,
  t: TFunction,
  nowMs?: number,
): PaymentStateLabel | null => {
  const state = latePaymentState(order, nowMs);
  if (!state) return null;

  const reason = order?.paidAfterExpiryReason === 'deadlinePassed'
    ? String(t(
      'orders.latePayment.reasonDeadlinePassed',
      'Płatność dotarła po upływie czasu na zapłatę, więc zamówienie zostało anulowane.',
    ))
    : String(t(
      'orders.latePayment.reasonOrderClosed',
      'Płatność dotarła, gdy zamówienie było już zamknięte, więc nie zostało przyjęte.',
    ));

  const byState: Record<LatePaymentState, PaymentStateLabel> = {
    refunded: {
      label: String(t('orders.latePayment.refunded', 'Wpłata po terminie, zwrócona automatycznie')),
      tone: 'ok',
      hint: String(t(
        'orders.latePayment.hintRefunded',
        'Pieniądze wróciły do gościa automatycznie, nie trzeba nic robić.',
      )),
    },
    refunding: {
      label: String(t('orders.latePayment.refunding', 'Wpłata po terminie, zwrot w toku')),
      tone: 'warn',
      hint: String(t(
        'orders.latePayment.hintRefunding',
        'Pieniądze wracają do gościa automatycznie, nie trzeba robić zwrotu ręcznie.',
      )),
    },
    refundFailed: {
      label: String(t('orders.latePayment.refundFailed', 'Wpłata po terminie, zwrot nieudany')),
      tone: 'bad',
      hint: String(t(
        'orders.latePayment.hintRefundFailed',
        'Automatyczny zwrot się nie udał. Zleć zwrot ręcznie.',
      )),
    },
    refundDelayed: {
      label: String(t('orders.latePayment.refundDelayed', 'Wpłata po terminie, zwrot się opóźnia')),
      tone: 'bad',
      hint: String(t(
        'orders.latePayment.hintRefundDelayed',
        'Zwrot automatyczny nie został potwierdzony w ciągu 30 minut. Sprawdź płatność w Stripe i w razie potrzeby zleć zwrot ręcznie.',
      )),
    },
  };

  const { label, tone, hint } = byState[state];
  return { label, tone, hint: `${reason} ${hint}` };
};

/**
 * Stan płatności do pokazania przy zamówieniu: JEDNO źródło dla list, kanbanów,
 * szczegółów i dashboardu. Spóźniona wpłata ma pierwszeństwo, bo samo „Opłacone"
 * przy anulowanym zamówieniu wprowadzało recepcję w błąd.
 */
export const getPaymentStateLabel = (
  order: OrderPaymentLike & LatePaymentLike & PaymentContextLike,
  t: TFunction,
  nowMs?: number,
): PaymentStateLabel | null => {
  if (order.paymentMethod !== 'online') return null;
  return latePaymentLabel(order, t, nowMs) ?? paymentStateBadge(order, t);
};
